import styled from '@emotion/styled/macro';
import rem from '../../utils/rem/rem';
import { StyledProps } from '../Table/Table.types';

export const Container = styled.div<StyledProps>`
  height: ${({ height }) => height};
  width: ${({ width }) => width};
`;

export const GridThemeWrapper = styled.div<StyledProps>`
  height: calc(
    ${({ height, showHeader, showFooter, headerInputButton }) =>
      showHeader && showFooter && headerInputButton
        ? /* inner table height = height - size of header with buttons and footer */
          `${height} - ${rem(125)}`
        : showHeader && showFooter
        ? /* inner table height = height - size of header and footer */
          `${height} - ${rem(112)}`
        : !showHeader && showFooter
        ? /* inner table height = height - size of footer */
          `${height} - ${rem(52)}`
        : showHeader && !showFooter
        ? /* inner table height = height - size of header */
          `${height} - ${rem(48)}`
        : /* inner table height  = height - size of header and footer */
          `${height} - ${rem(100)}`}
  );

  /* Rounded corners if header does not exist */
  .ag-root-wrapper {
    border-color: ${({ theme }) => theme.colors.gray400};
    border-top-left-radius: ${({ showHeader }) => (showHeader ? '0px' : '8px')};
    border-top-right-radius: ${({ showHeader }) => (showHeader ? '0px' : '8px')};
    border-bottom-left-radius: ${({ showFooter }) => (showFooter ? '0px' : '8px')};
    border-bottom-right-radius: ${({ showFooter }) => (showFooter ? '0px' : '8px')};
  }

  .ag-header {
    border-bottom: solid 1px ${({ theme }) => theme.colors.gray400};
  }

  /* Reduce padding around text for column headers */
  .ag-header-cell {
    padding-left: 10px;
    padding-right: 10px;
  }

  /* Remove bottom border for column headers */
  .ag-header-row:not(div:first-of-type) .ag-header-cell {
    border-top: none;
  }

  /* Header separators color and height */
  .ag-header-cell-resize::after {
    background-color: ${({ theme }) => theme.colors.gray400};
    height: 60%;
    top: calc(50% - 30%);
  }

  .ag-header-cell-text {
    font-family: 'Open Sans';
  }

  .ag-cell {
    border: none;
    font-family: 'Open Sans';
  }

  .ag-row {
    border: none;
  }

  /* Alternating row colors */
  .ag-row-last {
    border-bottom: solid 1px ${({ theme }) => theme.colors.gray400};
  }

  .ag-row-even {
    background-color: ${({ theme }) => theme.colors.white};
    border-top: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: 1px solid ${({ theme }) => theme.colors.white};
  }

  .ag-row-odd {
    background-color: ${({ theme }) => theme.colors.gray100};
    border-top: 1px solid ${({ theme }) => theme.colors.gray100};
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
  }

  .ag-row-hover {
    background-color: ${({ theme }) => theme.colors.blueHover};
  }

  .ag-row-selected {
    background-color: ${({ theme }) => theme.colors.blueHover};
    border-top: solid 1px ${({ theme }) => theme.colors.white};
    border-bottom: solid 1px ${({ theme }) => theme.colors.white};
  }

  /* Drop shadow on edit column */
  .ag-pinned-right-header {
    background-color: ${({ theme }) => theme.colors.gray400};
    box-shadow: 0px 0px 5px rgb(0 0 0 / 30%);
  }

  .ag-pinned-right-cols-container {
    border-left: solid 1px ${({ theme }) => theme.colors.gray400}; // remove?
    box-shadow: 0px 0px 5px rgb(0 0 0 / 30%);
  }

  .ag-checkbox-input-wrapper.ag-checked::after {
    color: ${({ theme }) => theme.colors.gray900};
  }

  .ag-input-field-input.ag-text-field-input {
    height: 35px;
  }

  /* Spacing between input box and filter icon */
  .ag-ltr .ag-floating-filter-button {
    margin-left: 5px;
  }

  /* Rounded borders and border color for filter input */
  input[class^='ag-'][type='text'] {
    border-color: ${({ theme }) => theme.colors.gray600};
    border-radius: 8px !important;
  }

  /* Remove ability to click into filter menu  */
  .ag-floating-filter-button-button {
    pointer-events: none;
  }

  /* Remove extra filter icon in column header when filtering */
  .ag-header-icon.ag-header-label-icon.ag-filter-icon {
    display: none;
  }

  /* Remove filter hamburger menu */
  .ag-icon-menu::before {
    content: '';
  }

  .ag-center-cols-viewport {
    background-color: ${({ theme }) => theme.colors.gray200};
  }
`;

export const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${({ theme }) => theme.colors.gray400};
  border-left: 1px solid ${({ theme }) => theme.colors.gray400};
  border-right: 1px solid ${({ theme }) => theme.colors.gray400};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  button {
    margin: ${rem(16)};
  }
`;

export const HeaderInputButton = styled.span`
  padding: 6px 16px 10px 12px;

  button:first-of-type {
    margin: 0;
    margin-top: ${rem(10)};
  }
`;

export const StyledHeaderTitle = styled.div`
  font-family: 'Open Sans';
  font-weight: 600;
  font-size: ${rem(20)};
  margin: 16px;
`;

export const StyledSubTitle = styled.div`
  font-family: 'Open Sans';
  font-weight: 400;
  font-size: ${rem(16)};
  margin: 16px;
`;

export const StyledFooter = styled.div<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: ${({ footerButtons }) => (footerButtons && footerButtons.length < 3 ? 'space-between' : 'flex-end')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray400};
  border-left: 1px solid ${({ theme }) => theme.colors.gray400};
  border-right: 1px solid ${({ theme }) => theme.colors.gray400};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;

  button {
    margin: ${rem(16)};
  }
`;

export const FooterButtons = styled.div`
  display: flex;
  justify-content: space-between;
  border-left: 1px solid ${({ theme }) => theme.colors.gray400};
  border-right: 1px solid ${({ theme }) => theme.colors.gray400};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray400};

  button {
    margin: ${rem(16)};
  }
`;

export const StyledReadOnlyLabel = styled.div<StyledProps>`
  font-family: 'Open Sans';
  justify-content: ${({ readOnlyLabel }) => ((readOnlyLabel && readOnlyLabel.length > 0) ? 'space-between' : 'flex-end')};
  display: flex;
  font-weight: 400;
  font-size: ${rem(13)};
  margin: ${rem(16)};
  font-style: italic;
  margin-right: 0px;
`;

export const StyledFooterText = styled.div`
  font-family: 'Open Sans';
  font-weight: 400;
  font-size: ${rem(14)};
  margin: ${rem(16)};
`;

export const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-content: center;
  align-items: center;

  svg {
    margin: ${rem(7)};
    cursor: pointer;
  }
`;
